import {
	CheckMarkGreenUnfilled,
	CloseThin,
	EllipsisOrange
} from '@/resources/assets/images';
import { useEffect, useMemo, useState } from 'react';
import BusinessPreviewEmailModal from '@/modules/dashboard/components/BusinessPreviewEmailModal/BusinessPreviewEmailModal';
import type { Business } from '@/modules/dashboard/dashboardTypes';
import CroppedText from '@/components/CroppedText/CroppedText';
import RichEditor from '@/components/RichEditor/RichEditor';
import Button from '@/components/Button/Button';
import { getSequencesAISnippets } from '@/services/sequencesService';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import type { RootState } from '@/rootReducer';
import Loader from '@/components/Loader/Loader';
import { sendAnalytics } from '@/services/analyticsService';
import { useIsScreenWidthSmallerThan } from '@/hooks/useIsScreenWidthSmallerThan';
import { cn } from '@/lib/classNames';
import { UserRole } from '@/modules/user/userTypes';
import { useUpdateOnboardingStatus } from '@/modules/onboarding/hooks/useUpdateOnboardingStatus';
import { useSequences } from '../hooks/useSequences';
import { emailGetMonthlyEmailLimit } from '../../email/emailActions';
import { useBusinessInfiniteScroll } from '../hooks/useBusinessInfiniteScroll';
import InfiniteScroll from '../components/InfiniteScroll';
import { useSequencesContext } from '../SequencesContext';
import { useGetOnboardingScore } from '../hooks/useGetOnboardingScore';
import { useSendAllSequence } from '../hooks/useSendAllSequences';
import { useOnboardingScore } from '../hooks/useOnboardingScore';
import CalendlyModal from '../components/CalendlyModal';

export default function SequencesSendAll() {
	const {
		selectedSequence,
		setShowSendAll,
		selectedBusinesses,
		selectedLists,
		businessesToRemove,
		businessesToInclude,
		modifiedEmails,
		unapprovedBusinesses,
		setModifiedEmails,
		businessesAlreadyInSequence,
		allBusinessesSelect,
		setIsEnrollOpen
	} = useSequencesContext();

	const { isBusinessesLoading } = useSequencesContext();
	const [user, monthlyLimit] = useSelector(
		(state: RootState) => [
			state.user.getIn(['authorize', 'user']).toJS(),
			state.email.getIn(['email', 'monthlyLimit'])
		],
		shallowEqual
	);

	const isUserOnboarding = user?.onboarding?.status === 'pending';

	const dispatch = useDispatch();

	const { onIntersect } = useBusinessInfiniteScroll();

	const [selectedBusinessToDisplay, setSelectedBusinessToDisplay] =
		useState<Business>(selectedBusinesses[0]);
	const [businessVisible, setBusinessVisible] = useState(false);

	const screenSize = useIsScreenWidthSmallerThan(1024);

	useEffect(() => {
		dispatch(emailGetMonthlyEmailLimit());
	}, []);

	function goBack() {
		setModifiedEmails([]);
		setShowSendAll(false);
		setIsEnrollOpen(true);
		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: isUserOnboarding
				? 'Onboarding Flow - Clicked Go Back on Sequence Review Screen'
				: 'Clicked Go Back on Sequence Review Screen',
			properties: {
				isUserOnboarding
			}
		});
	}

	const { triggerSendAll, isLoading } = useSendAllSequence();
	const { score, loading: scoreLoading } = useOnboardingScore();
	const [showCalendly, setShowCalendly] = useState(false);
	const handleCalendlyClose = () => {
		setShowCalendly(false);
	};
	const handleTriggerSendAll = async () => {
		if (scoreLoading) return;

		if (score && score >= 3) {
			setShowCalendly(true);
			return;
		}

		await triggerSendAll();
	};

	const listNames = selectedLists.map(l => l.name).join(', ');

	const availableBusinessesToSend = useMemo(
		() =>
			selectedBusinesses.filter(
				b =>
					!businessesAlreadyInSequence.has(b._id) &&
					(b.email ||
						b.facebook?.email ||
						b.web?.email ||
						(b.userAddedData?.map(
							a => a.type === 'email' && a.checked
						).length || 0) > 0)
			),
		[selectedBusinesses, businessesAlreadyInSequence]
	);

	const changeEmailToSend = (email: string) => {
		setModifiedEmails(
			modifiedEmails.map(m =>
				m.businessId === selectedBusinessToDisplay._id
					? { ...m, email }
					: m
			)
		);
	};

	const emailToSend = useMemo(
		() =>
			modifiedEmails.find(
				m => m.businessId === selectedBusinessToDisplay._id
			)?.email ||
			selectedBusinessToDisplay.email ||
			selectedBusinessToDisplay.facebook?.email ||
			selectedBusinessToDisplay.web?.email ||
			selectedBusinessToDisplay.userAddedData?.find(
				a => a.type === 'email' && a.checked
			)?.data ||
			'',
		[selectedBusinessToDisplay, modifiedEmails]
	);

	const businessesInSelectedListsCount = selectedLists.reduce((sum, list) => {
		return (
			sum +
			list.businessIds.filter(businessId => {
				// Check if the business is included for sending
				const isIncluded =
					allBusinessesSelect === 'SELECTED_ALL'
						? !businessesToRemove.includes(businessId)
						: businessesToInclude.some(
								b => b.businessId === businessId
							);

				return (
					isIncluded &&
					!businessesAlreadyInSequence.has(businessId) &&
					!unapprovedBusinesses.includes(businessId)
				);
			}).length
		);
	}, 0);

	const activeBusinessesInCurrentSequence =
		selectedSequence?.businesses?.reduce(
			(sum, business) => (business.status === 'ACTIVE' ? sum + 1 : sum),
			0
		) || 0;

	const noBusinessSelected =
		availableBusinessesToSend.length === 0 ||
		availableBusinessesToSend.length === unapprovedBusinesses.length;

	return (
		<>
			<div className="fixed left-0 top-0 z-150 flex h-screen w-screen items-center justify-center bg-black bg-opacity-50">
				<div
					style={{
						maxWidth: '1300px'
					}}
					className={cn(
						'relative flex h-4/5 w-[calc(100vw-20px)] flex-col rounded-lg border border-dark-grey bg-dm-black p-4 lg:w-4/5 lg:px-10 lg:pb-8 lg:pt-12',
						screenSize.isSmaller && businessVisible && 'border-none'
					)}
				>
					<CloseButton closeSendAllModal={goBack} />
					<div>
						<span className="block w-[90%] text-2xl text-xl text-orange lg:w-full lg:text-2xl">
							<CroppedText text={listNames} maxLength={70} />
						</span>
						<span className="mb-4 block text-sm text-gray-100 lg:mb-2 lg:text-base">
							(Only businesses with emails appear)
						</span>
						<div>
							{businessesInSelectedListsCount -
								unapprovedBusinesses.length >
								monthlyLimit || user.role === UserRole.PreTrial
								? `Your sequence has ${businessesInSelectedListsCount + activeBusinessesInCurrentSequence - unapprovedBusinesses.length} businesses, and you can schedule a max of ${monthlyLimit} businesses`
								: ''}
						</div>
					</div>
					<div className="m-auto flex h-full w-full overflow-hidden rounded-2xl border border-dark-grey">
						<div className="flex w-full flex-col overflow-y-auto overflow-x-hidden border-r border-dark-grey lg:w-[43.75rem]">
							{availableBusinessesToSend.map(b => (
								<BusinessInSendAllItem
									key={b._id}
									isSelected={
										selectedBusinessToDisplay._id === b._id
									}
									business={b}
									setSelectedBusinessToDisplay={
										setSelectedBusinessToDisplay
									}
									setBusinessVisible={setBusinessVisible}
								/>
							))}
							<InfiniteScroll onIntersect={onIntersect} />
							{isBusinessesLoading && (
								<tr className="flex h-8 items-center justify-center">
									<td>
										<Loader className="w-5" />
									</td>
								</tr>
							)}
						</div>
						<div
							className={cn(
								'absolute left-0 top-0 h-full w-[calc(100vw-20px)] overflow-x-hidden rounded-lg border border-dark-grey bg-dm-black lg:static lg:h-auto lg:w-[43.75rem] lg:rounded-none lg:border-r lg:border-none',
								screenSize.isSmaller &&
									!businessVisible &&
									'hidden'
							)}
						>
							<BusinessPreviewEmailModal
								user={user}
								setIsVisible={setBusinessVisible}
								business={selectedBusinessToDisplay}
								to={emailToSend}
								setTo={changeEmailToSend}
								noEdit
							/>
						</div>
						<SendAllEmailEdit
							selectedBusinessToDisplay={
								selectedBusinessToDisplay
							}
							emailToSend={emailToSend}
						/>
						{showCalendly && (
							<CalendlyModal
								calendlyUrl="https://calendly.com/d/cqdm-gr6-4yr?utm_source=trial"
								onClose={handleCalendlyClose}
							/>
						)}
					</div>
					<div className="mt-5 flex justify-end gap-16">
						<button
							type="button"
							onClick={goBack}
							className="text-lg font-medium text-orange"
						>
							Go back
						</button>
						<Button
							onClick={handleTriggerSendAll}
							className="w-min"
							disabled={
								isLoading ||
								noBusinessSelected ||
								businessesInSelectedListsCount -
									unapprovedBusinesses.length >
									monthlyLimit
							}
						>
							{isLoading
								? 'Initiating...'
								: businessesInSelectedListsCount -
											unapprovedBusinesses.length >
									  monthlyLimit
									? `Remove ${businessesInSelectedListsCount - unapprovedBusinesses.length - monthlyLimit} businesses to initiate`
									: 'Set sequence live'}
						</Button>
					</div>
				</div>
			</div>
		</>
	);
}

function SendAllEmailEdit({
	selectedBusinessToDisplay,
	emailToSend
}: {
	selectedBusinessToDisplay: Business;
	emailToSend: string;
}) {
	const {
		selectedSequence,
		businessesToRemove,
		setUnapprovedBusinesses,
		modifiedEmails,
		setModifiedEmails
	} = useSequencesContext();

	const [emailBody, setEmailBody] = useState('');
	const [emailSubject, setEmailSubject] = useState('');
	const [loadingAISnippet, setLoadingAISnippet] = useState(false);

	const replaceVariables = (value: string): string => {
		let updatedValue = value?.replaceAll(
			/\[business_name\]/gi,
			selectedBusinessToDisplay?.name ?? ''
		);
		updatedValue = updatedValue?.replaceAll(
			/\[list_name\]/gi,
			selectedBusinessToDisplay?.listName ?? ''
		);
		return updatedValue;
	};

	const isSelected = !businessesToRemove.includes(
		selectedBusinessToDisplay._id
	);

	const firstStepOfSequence = selectedSequence?.steps[0];

	const bodyReplaced = replaceVariables(firstStepOfSequence?.body || '');
	const subjectReplaced = replaceVariables(
		firstStepOfSequence?.subject || ''
	);

	const hasAISnippet = bodyReplaced.includes('[ai_snippet]');

	const hasToBeGenerated =
		hasAISnippet &&
		!modifiedEmails
			.map(b => b.businessId)
			.includes(selectedBusinessToDisplay._id);

	useEffect(() => {
		setEmailSubject(subjectReplaced);
		if (hasAISnippet && hasToBeGenerated) {
			setLoadingAISnippet(true);
			getSequencesAISnippets({
				businessId: selectedBusinessToDisplay._id,
				emailBody: bodyReplaced
			})
				.then(res => {
					if (res.data) {
						const aiSnippedModified = replaceVariables(
							bodyReplaced.replace(
								'[ai_snippet]',
								res.data.data.snippet
							)
						);
						setEmailBody(aiSnippedModified);
						// @ts-ignore
						setModifiedEmails(prev => [
							...prev,
							{
								businessId: selectedBusinessToDisplay._id,
								subject: subjectReplaced,
								body: aiSnippedModified,
								emailToSend
							}
						]);
					}
				})
				.finally(() => setLoadingAISnippet(false));
			return;
		}
		// If the business has an email already created, we use it
		const existingEmailCreated = modifiedEmails.find(
			email => email.businessId === selectedBusinessToDisplay._id
		);
		if (existingEmailCreated) {
			setEmailBody(existingEmailCreated.body);
			setEmailSubject(existingEmailCreated.subject);
			return;
		}
		if (!existingEmailCreated) {
			// @ts-ignore
			setModifiedEmails(prev => [
				...prev,
				{
					businessId: selectedBusinessToDisplay._id,
					subject: subjectReplaced,
					body: bodyReplaced,
					email: emailToSend
				}
			]);
			setEmailBody(bodyReplaced);
		}
	}, [selectedBusinessToDisplay]);

	function handleUnapproveEmail() {
		if (isSelected) {
			// @ts-ignore
			setUnapprovedBusinesses(prev => [
				...prev,
				selectedBusinessToDisplay._id
			]);
			return;
		}

		// @ts-ignore
		setUnapprovedBusinesses(prev =>
			// @ts-ignore
			prev.filter(id => id !== selectedBusinessToDisplay._id)
		);
	}

	return (
		<div className="hidden h-full w-full flex-col gap-4 overflow-x-hidden p-7 lg:flex">
			<input
				type="text"
				className="w-full bg-gray-200 text-dm-black"
				value={emailSubject}
				onChange={e => {
					setEmailSubject(e.target.value);

					setModifiedEmails(
						modifiedEmails.map(m =>
							m.businessId === selectedBusinessToDisplay._id
								? { ...m, subject: e.target.value }
								: m
						)
					);
				}}
			/>
			{loadingAISnippet ? (
				<div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gray-200">
					<Loader />
					<span>Generating AI Snippet</span>
				</div>
			) : (
				<RichEditor
					onChange={e => {
						setEmailBody(e);
						setModifiedEmails(
							modifiedEmails.map(m =>
								m.businessId === selectedBusinessToDisplay._id
									? { ...m, body: e }
									: m
							)
						);
					}}
					value={emailBody}
					className="h-full w-full bg-gray-200 text-dm-black"
					disableDisabledStyles
				/>
			)}

			<button
				onClick={handleUnapproveEmail}
				className="w-min rounded-xl border border-orange-dark px-8 py-2 text-sm text-orange"
			>
				{isSelected ? 'Unapprove' : 'Approve'}
			</button>
		</div>
	);
}

function BusinessInSendAllItem({
	isSelected,
	business,
	setSelectedBusinessToDisplay,
	setBusinessVisible
}: {
	isSelected: boolean;
	business: Business;
	setSelectedBusinessToDisplay: (business: Business) => void;
	setBusinessVisible: (visible: boolean) => void;
}) {
	const screenSize = useIsScreenWidthSmallerThan(1024);

	function selectBusinessToDisplay() {
		setSelectedBusinessToDisplay(business);

		if (screenSize.isSmaller) {
			setBusinessVisible(true);
		}
	}
	return (
		<div
			role="button"
			tabIndex={0}
			onKeyDown={e => {
				if (e.key === 'Enter') {
					selectBusinessToDisplay();
				}
			}}
			onClick={selectBusinessToDisplay}
			className={cn(
				'flex h-16 w-full flex-shrink-0 cursor-default items-center justify-between border-b-2 border-blue-600 px-4 text-left lg:justify-around',
				isSelected
					? 'bg-orange text-black'
					: 'bg-dm-black3.5 text-white hover:bg-gray-600'
			)}
		>
			<CroppedText text={business.name} maxLength={25} />
			<Toggle businessId={business._id} />
		</div>
	);
}

function Toggle({ businessId }: { businessId: string }) {
	const { unapprovedBusinesses, setUnapprovedBusinesses } =
		useSequencesContext();
	const isSelected = !unapprovedBusinesses.includes(businessId);

	function handleClick(e: any) {
		e.stopPropagation();

		if (isSelected) {
			// @ts-ignore
			setUnapprovedBusinesses(prev => [...prev, businessId]);
			return;
		}
		// @ts-ignore
		setUnapprovedBusinesses(prev => prev.filter(id => id !== businessId));
	}

	const transformValue = isSelected ? 'translateX(55%)' : 'translateX(0)';

	return (
		<button
			onClick={handleClick}
			className="relative h-7 w-16 rounded-xl border border-dark-grey bg-dm-black"
		>
			<div
				style={{
					height: '110%',
					transform: `${transformValue} translateY(2%)`,
					transition: 'transform 0.2s ease-in-out'
				}}
				className="absolute bottom-0 top-0 my-auto flex w-4/6 items-center justify-center rounded-xl border bg-white"
			>
				{isSelected ? <CheckMarkGreenUnfilled /> : <EllipsisOrange />}
			</div>
		</button>
	);
}

function CloseButton({ closeSendAllModal }: { closeSendAllModal: () => void }) {
	return (
		<button className="absolute right-8 top-4">
			<CloseThin
				className="h-4 w-4 text-gray-100"
				onClick={closeSendAllModal}
			/>
		</button>
	);
}
